/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Carousel } from '@mantine/carousel';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import WsSectionContainer from '../WsSectionContainer';
import {
  IMAGE_DISPLAY_ENUM,
  QUICK_ADD_STYLE_ENUM,
  VIEW_ALL_STYLE_ENUM
} from './wsFeaturedDesignConfig';
import AppFlexbox from '../../../../common/AppFlexbox';
import HoverableAnchor from '../../../../common/HoverableAnchor';
import WsProductCard from '../productGrid/WsProductCard';
import { sterilizeUrlHandle } from '../../../../../helpers/format';
import {
  getSelectedResourceBaseNavigationUrl,
  getWebstoreHeadingSize
} from '../../../../../helpers/webstoreHelper';

const WsFeaturedDesign = ({
  config,
  colorSchemes,
  themeConfig,
  baseStoreUrl,
  onNavigate,
  ecomStoreTheme,
  mediaQueries,
  resourceNavigation,
  ...rest
}) => {
  const { state } = useContext(StoreUserContext);
  const { isTabletOrSmaller } = mediaQueries;

  const ecomStoreDesign = state.ecomStoreDesignHistory.value.find(
    (c) =>
      c.pkEcomVendorDesign.toString() === config.ecomStoreDesign?.toString()
  );
  const vendorProducts = config.ecomStoreDesign
    ? ecomStoreDesign?.ecomStoreDesignProducts.map((p) => p.ecomStoreProduct) ??
      []
    : state.paginatedEcomStoreProductsPreview.data;

  const loading = config.ecomStoreDesign
    ? !ecomStoreDesign || state.ecomStoreDesignHistory.loading
    : !state.paginatedEcomStoreProductsPreview.initialized ||
      state.paginatedEcomStoreProductsPreview.loading;

  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const showSecondImageOnHover = config.showSecondImageOnHover === 'true';
  const showVendorName = config.showVendorName === 'true';
  const showVariantsSeparately = config.showVariantsSeparately === 'true';
  const variantSelector = config.seperateVariantsSelector;
  const maxDisplayed = config.maximumProductsDisplayed * 1 ?? 25;
  const squareRatio = config.imageRatio === IMAGE_DISPLAY_ENUM.SQUARE;
  const isQuickAddEnabled =
    config.quickAddStyle === QUICK_ADD_STYLE_ENUM.STANDARD;

  const designNotFound = state.ecomStoreDesignHistory.idsNotFound.includes(
    config.ecomStoreDesign?.toString()
  );

  const hideAllProductBadges = themeConfig.hideAllProductBadges === 'true';
  const hideFreeShippingBadge = themeConfig.hideFreeShippingBadge === 'true';
  const baseProductsResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    'products'
  )}`;

  return (
    <WsSectionContainer
      colorScheme={colorScheme}
      config={config}
      mediaQueries={mediaQueries}
      themeConfig={themeConfig}
      withPadding
      {...rest}
    >
      <AppStack
        style={{
          flex: 1,
          justifyContent: 'space-between',
          margin: 'auto',
          width: '100%',
          gap: 8
        }}
      >
        {config.headingText && (
          <AppText
            style={{
              fontWeight: 600,
              fontSize: getWebstoreHeadingSize(config.headingSize)
            }}
          >
            {config.headingText}
          </AppText>
        )}
        {config.descriptionText && (
          <AppText style={{ fontSize: 16 }}>
            {config.showDesignDescription === 'true'
              ? ecomStoreDesign?.description
              : config.descriptionText}
          </AppText>
        )}
        <AppStack
          style={{
            flex: 1,
            justifyContent: 'space-between',
            margin: 'auto',
            width: '100%',
            gap: 0
          }}
        >
          {designNotFound || (!loading && vendorProducts.length === 0) ? (
            <AppStack style={{ alignItems: 'center', padding: 40 }}>
              <AppText
                style={{
                  color: colorScheme.textColor,
                  fontSize: 18,
                  fontWeight: '500'
                }}
              >
                No products found
              </AppText>
            </AppStack>
          ) : (isTabletOrSmaller && config.mobileCarousel === 'true') ||
            (!isTabletOrSmaller && config.desktopCarousel === 'true') ? (
            <Carousel
              align={isTabletOrSmaller ? 'center' : 'start'}
              controlSize={40}
              draggable
              loop={false}
              slideGap={16}
              slideSize={`${
                (isTabletOrSmaller ? 85 : 100) /
                (isTabletOrSmaller
                  ? config.mobileColumnCount * 1 ?? 1
                  : config.desktopColumnCount * 1 ?? 6)
              }%`}
              slidesToScroll={
                isTabletOrSmaller
                  ? config.mobileColumnCount * 1 ?? 1
                  : config.desktopColumnCount * 1 ?? 6
              }
              withControls={!isTabletOrSmaller}
              withIndicators={false}
            >
              {loading
                ? Array.from(Array(config.desktopColumnCount * 1 ?? 6))
                    .slice(0, maxDisplayed)
                    .map((x, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Carousel.Slide key={i}>
                        <WsProductCard
                          baseStoreUrl={baseProductsResourceUrl}
                          colorScheme={colorScheme}
                          hideAllProductBadges={hideAllProductBadges}
                          hideFreeShippingBadge={hideFreeShippingBadge}
                          isQuickAddEnabled={isQuickAddEnabled}
                          onNavigate={onNavigate}
                          portraitRatio={!squareRatio}
                          showSecondImageOnHover={showSecondImageOnHover}
                          showVendorName={showVendorName}
                        />
                      </Carousel.Slide>
                    ))
                : vendorProducts.slice(0, maxDisplayed).map((p) => (
                    <Carousel.Slide key={p.pkEcomStoreProduct}>
                      <WsProductCard
                        baseStoreUrl={baseProductsResourceUrl}
                        colorScheme={colorScheme}
                        ecomStoreProduct={p}
                        hideAllProductBadges={hideAllProductBadges}
                        hideFreeShippingBadge={hideFreeShippingBadge}
                        isQuickAddEnabled={isQuickAddEnabled}
                        onNavigate={onNavigate}
                        portraitRatio={!squareRatio}
                        showSecondImageOnHover={showSecondImageOnHover}
                        showVendorName={showVendorName}
                      />
                    </Carousel.Slide>
                  ))}
            </Carousel>
          ) : (
            <AppStack style={{}}>
              <Grid
                columns={
                  isTabletOrSmaller
                    ? config.mobileColumnCount * 1 ?? 1
                    : config.desktopColumnCount * 1 ?? 6
                }
              >
                {loading
                  ? Array.from(Array(config.desktopColumnCount * 1 ?? 6))
                      .slice(0, maxDisplayed)
                      .map((x, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Grid.Col key={i} span={1}>
                          <WsProductCard
                            baseStoreUrl={baseProductsResourceUrl}
                            colorScheme={colorScheme}
                            hideAllProductBadges={hideAllProductBadges}
                            hideFreeShippingBadge={hideFreeShippingBadge}
                            isQuickAddEnabled={isQuickAddEnabled}
                            onNavigate={onNavigate}
                            portraitRatio={!squareRatio}
                            showSecondImageOnHover={showSecondImageOnHover}
                            showVendorName={showVendorName}
                          />
                        </Grid.Col>
                      ))
                  : vendorProducts.slice(0, maxDisplayed).map((p) => (
                      <Grid.Col key={p.pkEcomStoreProduct} span={1}>
                        <WsProductCard
                          baseStoreUrl={baseProductsResourceUrl}
                          colorScheme={colorScheme}
                          ecomStoreProduct={p}
                          hideAllProductBadges={hideAllProductBadges}
                          hideFreeShippingBadge={hideFreeShippingBadge}
                          isQuickAddEnabled={isQuickAddEnabled}
                          onNavigate={onNavigate}
                          portraitRatio={!squareRatio}
                          showSecondImageOnHover={showSecondImageOnHover}
                          showVendorName={showVendorName}
                        />
                      </Grid.Col>
                    ))}
              </Grid>
            </AppStack>
          )}

          {config.enableViewAll === 'true' &&
            (ecomStoreDesign
              ? ecomStoreDesign.ecomStoreDesignProductCount
              : state.paginatedEcomStoreProductsPreview.totalCount) >
              maxDisplayed && (
              <AppFlexbox
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 20
                }}
              >
                {config.viewAllButtonStyle === VIEW_ALL_STYLE_ENUM.LINK ? (
                  <HoverableAnchor
                    component={Link}
                    onClick={onNavigate}
                    style={{ color: colorScheme.textColor }}
                    to={
                      ecomStoreDesign
                        ? `${baseStoreUrl}/designs/${sterilizeUrlHandle(
                            ecomStoreDesign.name
                          )}`
                        : `${baseStoreUrl}/products`
                    }
                  >
                    View all
                  </HoverableAnchor>
                ) : config.viewAllButtonStyle ===
                  VIEW_ALL_STYLE_ENUM.OUTLINE_BUTTON ? (
                  <Button
                    color={colorScheme.outlineButtonColor}
                    component={Link}
                    onClick={onNavigate}
                    radius={0}
                    size="md"
                    to={
                      ecomStoreDesign
                        ? `${baseStoreUrl}/designs/${sterilizeUrlHandle(
                            ecomStoreDesign.name
                          )}`
                        : `${baseStoreUrl}/products`
                    }
                    variant="outline"
                  >
                    View all
                  </Button>
                ) : (
                  <Button
                    color={colorScheme.solidButtonColor}
                    component={Link}
                    onClick={onNavigate}
                    radius={0}
                    size="md"
                    style={{ color: colorScheme.solidButtonLabel }}
                    to={
                      ecomStoreDesign
                        ? `${baseStoreUrl}/designs/${sterilizeUrlHandle(
                            ecomStoreDesign.name
                          )}`
                        : `${baseStoreUrl}/products`
                    }
                    variant="filled"
                  >
                    View all
                  </Button>
                )}
              </AppFlexbox>
            )}
        </AppStack>
      </AppStack>
    </WsSectionContainer>
  );
};

WsFeaturedDesign.propTypes = {
  baseStoreUrl: PropTypes.string,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  resourceNavigation: PropTypes.array,
  themeConfig: PropTypes.object
};

export default WsFeaturedDesign;
